import { Injectable } from '@angular/core';

/**
 * libs
 */
import { saveAs } from 'file-saver';

/**
 * interfaces
 */
import { ExportFormat } from '../types';
import { IImageExporter, IImageExportRequest } from '../interfaces';

/**
 * utils
 */
import { ensureFileExtension } from './export-row.utils';
import {
  EXPORT_IMAGE_TYPE_DEFAULT,
  EXPORT_IMAGE_TYPE_EXTENSION,
  captureElementToBlob,
} from '../utilities/dom-capture.util';

/**
 * Image export strategy implementation.
 *
 * @remarks
 * The only strategy that exports what is *rendered* rather than what is in the
 * model, so it reads `metadata.element` and ignores `rows`, `columns` and
 * `sections` entirely. Because the browser paints the capture, Arabic shaping,
 * bidi ordering and RTL layout are exact without any work here.
 *
 * Registered under the single `image` format. The encoding is chosen by
 * `metadata.imageType` and the rasterizer by `request.engine`; neither is a
 * separate strategy, so PNG, JPEG, WebP and SVG all arrive here.
 */
@Injectable()
export class ImageExporterService implements IImageExporter {

  /**
   * The format of the exporter.
   */
  public readonly format: ExportFormat = 'image';

  /**
   * Export a rendered region to an image file.
   *
   * @param request - The export request. Reads `request.engine` and
   * `metadata.element` (required), `metadata.imageType`, `metadata.quality`,
   * `metadata.scale`, `metadata.excludeSelectors` and
   * `metadata.backgroundColor`.
   * @throws When no element was supplied, or the capture cannot be encoded.
   *
   * @remarks
   * Errors are allowed to propagate: the facade owns reporting, so a failure
   * here reaches the user as a toast and a console entry rather than being
   * swallowed the way the implementation this replaces swallowed it.
   *
   * `metadata` is required on {@link IImageExportRequest}, so it is read
   * without optional chaining. The element is still validated inside
   * `captureElementToBlob`: the type guarantees an `HTMLElement` was
   * *supplied*, not that it is attached to the document or has a visible box.
   *
   * The saved extension comes from {@link EXPORT_IMAGE_TYPE_EXTENSION} rather
   * than from `imageType` directly, because two of its members name a library
   * call (`canvas`, `Blob`) and both produce a PNG.
   */
  public async export(request: IImageExportRequest): Promise<void> {

    const metadata = request.metadata;
    const imageType = metadata.imageType ?? EXPORT_IMAGE_TYPE_DEFAULT;

    const blob = await captureElementToBlob(metadata.element, {
      imageType,
      quality: metadata.quality,
      engine: request.engine,
      scale: metadata.scale,
      excludeSelectors: metadata.excludeSelectors,
      backgroundColor: metadata.backgroundColor,
    });

    saveAs(blob, ensureFileExtension(request.fileName, `.${EXPORT_IMAGE_TYPE_EXTENSION[imageType]}`));
  }
}
